import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { Avatar } from '../../components/common/Avatar';
import { Loading } from '../../components/common/Loading';
import { getApiClient } from '../../api/client';
import { ROUTES } from '../../utils/constants';

interface ChatMessage {
  id: string;
  createdAt: number;
  senderId: string;
  senderName: string;
  senderAvatar: string | null;
  body: {
    type: string;
    text?: string;
  };
  replyToId?: string;
}

interface ChatTarget {
  id: string;
  name: string;
  avatarUrl: string | null;
  kind: 'user' | 'cohort';
}

interface ChatProps {
  tenant: string;
}

export function Chat({ tenant }: ChatProps) {
  const { targetId } = useParams<{ targetId: string }>();
  const navigate = useNavigate();
  const [target, setTarget] = useState<ChatTarget | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!targetId) return;

    loadChat();

    // Connect to WebSocket for real-time updates (only for cohorts)
    const connectWebSocket = async () => {
      const apiClient = getApiClient(tenant);
      const token = localStorage.getItem('auth_token');

      if (!token) return;

      // Only connect WebSocket for cohort chats
      if (target?.kind === 'cohort') {
        const apiUrl = (import.meta as any).env.VITE_API_URL as string | undefined;
        const wsUrl = `${apiUrl?.replace('https://', 'wss://').replace('http://', 'ws://')}/${tenant}/api/msg/cohort/${targetId}/ws?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('WebSocket connected');
        };

        ws.onmessage = (event) => {
          const notification = JSON.parse(event.data);
          if (notification.type === 'message') {
            // Reload chat to get new message
            loadChat();
          }
        };

        ws.onerror = (error) => {
          console.error('WebSocket error:', error);
        };

        ws.onclose = () => {
          console.log('WebSocket disconnected');
        };

        wsRef.current = ws;
      }
    };

    // Connect WebSocket after target is loaded
    if (target) {
      connectWebSocket();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [tenant, targetId, target]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChat = async () => {
    if (!targetId) return;

    try {
      const apiClient = getApiClient(tenant);
      const response = await apiClient.get<{
        target: ChatTarget;
        messages: ChatMessage[];
      }>(`/msg/chat/${targetId}`);

      setTarget(response.target);
      setMessages(response.messages);
    } catch (err) {
      console.error('Failed to load chat:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageText.trim() || !targetId) return;

    setIsSending(true);

    try {
      const apiClient = getApiClient(tenant);
      await apiClient.post('/msg/send', {
        targetId,
        body: {
          type: 'text',
          text: messageText.trim(),
        },
      });

      setMessageText('');
      loadChat();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return <Loading message="Loading chat..." />;
  }

  if (!target) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Chat not found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(ROUTES.home(tenant))}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Button>
        <Avatar src={target.avatarUrl} name={target.name} size="md" />
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{target.name}</h1>
          <p className="text-sm text-gray-500">
            {target.kind === 'cohort' ? 'Group Chat' : 'Direct Message'}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex gap-3">
              <Avatar
                src={message.senderAvatar}
                name={message.senderName}
                size="sm"
              />
              <div className="flex-1">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-medium text-sm text-gray-900">
                    {message.senderName}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(message.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {message.body.text || '[No content]'}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={isSending}
          />
          <Button type="submit" variant="primary" isLoading={isSending}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </Button>
        </div>
      </form>
    </div>
  );
}
